/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, TablePagination, CircularProgress, Typography,
  Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TableFooter, Card, CardActionArea, CardContent, MenuItem, InputAdornment,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  GetApp as DownloadIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  CalendarToday as CalIcon,
  FilterAltOff as ClearIcon,
  StorefrontOutlined as StoreIcon,
} from "@mui/icons-material";
import {
  PurchaseOrdersProvider,
  usePurchaseOrders,
} from "../../contexts/alfaroojfresh/PurchaseOrdersContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import STORE_NAMES_FF from "../../utils/STORE_NAMES_FF";

const NUM_STORES = 14;
const baseUrl = process.env.REACT_APP_BASE_URL;

// ─────────────────────────────────────────
// Theme  (matches Suppliers light-mode)
// ─────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f8fafc", paper: "#ffffff" },
    primary: { main: "#3753b3" },
    text: { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: { },
});

// ─────────────────────────────────────────
// Animations
// ─────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const pulse = keyframes`
  0%,100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.25); }
  50%      { box-shadow: 0 0 0 8px rgba(245,158,11,0); }
`;

// ─────────────────────────────────────────
// Styled components  (same palette as Suppliers)
// ─────────────────────────────────────────
const Root = styled("div")({
  minHeight: "100vh",
  background: "#f8fafc",
//  fontFamily: "'DM Mono', monospace",
  padding: "40px 48px",
  animation: `${fadeUp} 500ms ease both`,
});

const TitleBlock = styled("div")({
  "& .eyebrow": {
    fontSize: "0.65rem", letterSpacing: "0.25em",
    textTransform: "uppercase", color: "#3753b3", marginBottom: 6,
  },
  "& h1": {
    margin: 0, fontSize: "2rem", fontWeight: 700,
    color: "#0f172a", letterSpacing: "-0.03em", lineHeight: 1,
  },
  "& .sub": { fontSize: "0.75rem", color: "#94a3b8", marginTop: 6 },
});

const ActionBtn = styled(Button)({
  textTransform: "none",
 // fontFamily: "'DM Mono', monospace",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: 8,
  padding: "8px 16px",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  background: "#ffffff",
  transition: "all 0.2s",
  "&:hover": { borderColor: "#3753b3", color: "#3753b3", background: "rgba(245,158,11,0.05)" },
});

const StatCard = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "14px 18px",
  minWidth: 110,
  "& .val": { fontSize: "1.4rem", fontWeight: 700, color: "#0f172a", lineHeight: 1 },
  "& .lbl": { fontSize: "0.62rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#94a3b8", marginTop: 4 },
});

const StoreCardStyled = styled(Card, {
  shouldForwardProp: (p) => p !== "active",
})(({ active }) => ({
  width: 180,
  minWidth: 150,
  borderRadius: 12,
  border: active ? "1.5px solid #3753b3" : "1px solid #e2e8f0",
  background: active ? "rgba(245,158,11,0.05)" : "#ffffff",
  boxShadow: active ? "0 4px 18px rgba(245,158,11,0.12)" : "0 2px 8px rgba(15,23,42,0.04)",
  transform: active ? "translateY(-4px)" : "none",
  transition: "all 0.2s",
  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(11, 50, 245, 0.1)", borderColor: "#3753b3" },
}));

const FilterBar = styled("div")({
  display: "flex", gap: 12, marginBottom: 20,
  alignItems: "center", flexWrap: "wrap",
});

const SectionBox = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: "24px 28px",
  marginBottom: 20,
  boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
});

const SectionTitle = styled("div")({
  fontSize: "0.85rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "& .accent": { color: "#3753b3" },
});

const StyledTableContainer = styled(Paper)({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
  overflow: "hidden",
});

const tableHeadSx = {
  "& .MuiTableCell-head": {
    
    fontSize: "0.65rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "#94a3b8",
    background: "#f8fafc",
    fontWeight: 600,
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};

const tableBodySx = {
  "& .MuiTableRow-root": {
    transition: "background 0.15s",
    "&:hover": { background: "#fefce8" },
  },
  "& .MuiTableCell-body": {
    
    fontSize: "0.78rem",
    color: "#475569",
    padding: "12px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};

const RefChip = styled("span")({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 5,
  background: "#3753b3",
  border: "1px solid #09194d",
  color: "whiteSmoke",
  fontSize: "0.72rem",
  letterSpacing: "0.05em",
});

const inputSx = {
  "& .MuiOutlinedInput-root": {
    
    fontSize: "0.78rem",
    background: "#ffffff",
    borderRadius: 2,
    color: "#0f172a",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#3753b3" },
  },
  "& .MuiInputLabel-root": {  fontSize: "0.75rem", color: "#94a3b8" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3753b3" },
};

const menuPropsSx = {
  PaperProps: {
    sx: {
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 2,
      boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
      "& .MuiMenuItem-root": {
        
        fontSize: "0.78rem",
        color: "#475569",
        "&:hover": { background: "#fefce8", color: "#d97706" },
        "&.Mui-selected": { background: "rgba(245,158,11,0.08)", color: "#3753b3" },
      },
    },
  },
};

// ─────────────────────────────────────────
// Helpers to init per-store state arrays
// ─────────────────────────────────────────
const arr = (val) => Array.from({ length: NUM_STORES }, () => val);

// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────
const PurchaseOrdersTable = () => {
  const endpoints = Array.from({ length: NUM_STORES }, (_, i) =>
    process.env[`REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_${i}`]
  );

  const {
    purchaseOrders, purchaseOrders1, purchaseOrders2, purchaseOrders3,
    purchaseOrders4, purchaseOrders5, purchaseOrders6, purchaseOrders7,
    purchaseOrders8, purchaseOrders9, purchaseOrders10, purchaseOrders11,
    purchaseOrders12, purchaseOrders13,
    suppliers,
    total, total1, total2, total3, total4, total5, total6, total7,
    total8, total9, total10, total11, total12, total13,
    loading, error, goods, categories,
  } = usePurchaseOrders();

  // All per-store orders in an array for easy indexed access
  const allOrders = [
    purchaseOrders, purchaseOrders1, purchaseOrders2, purchaseOrders3,
    purchaseOrders4, purchaseOrders5, purchaseOrders6, purchaseOrders7,
    purchaseOrders8, purchaseOrders9, purchaseOrders10, purchaseOrders11,
    purchaseOrders12, purchaseOrders13,
  ];
  const allTotals = [
    total, total1, total2, total3, total4, total5, total6, total7,
    total8, total9, total10, total11, total12, total13,
  ];

  // ── Per-store state arrays (replaces 14× individual useState) ──
  const [selectedStores, setSelectedStores] = useState([]);
  const [searchTerms, setSearchTerms]     = useState(arr(""));
  const [filterFrom, setFilterFrom]       = useState(arr(""));
  const [filterTo, setFilterTo]           = useState(arr(""));
  const [supplierFilters, setSupplierFilters] = useState(arr(""));
  const [goodFilters, setGoodFilters]     = useState(arr(""));
  const [unitFilters, setUnitFilters]     = useState(arr(""));
  const [pages, setPages]                 = useState(arr(0));
  const [rowsPerPage, setRowsPerPage]     = useState(arr(10));

  // Global filters
  const [globalSupplierFilter, setGlobalSupplierFilter] = useState("");
  const [globalGoodFilter, setGlobalGoodFilter]         = useState("");
  const [globalUnitFilter, setGlobalUnitFilter]         = useState("");

  // Modal
  const [openModal, setOpenModal]   = useState(false);
  const [fetchedPO, setFetchedPO]   = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // ── Store names ──
  const getStoreFromUrl = (url) => {
    try { return new URL(url).searchParams.get("0_store"); }
    catch { return null; }
  };
  const storeNames = endpoints.map((ep) => STORE_NAMES_FF[getStoreFromUrl(ep)] || "Unknown Store");

  // ── Helpers to update indexed state ──
  const setAt = (setter) => (i, val) =>
    setter((prev) => { const next = [...prev]; next[i] = val; return next; });

  const setSearch     = setAt(setSearchTerms);
  const setFrom       = setAt(setFilterFrom);
  const setTo         = setAt(setFilterTo);
  const setSupplier   = setAt(setSupplierFilters);
  const setGood       = setAt(setGoodFilters);
  const setUnit       = setAt(setUnitFilters);
  const setPage       = setAt(setPages);
  const setRPP        = setAt(setRowsPerPage);

  // ── Toggle store card ──
  const toggleStore = (i) => {
    setSelectedStores((prev) =>
      prev.includes(i) ? prev.filter((s) => s !== i) : [...prev, i]
    );
  };

  // ── Filter logic (unchanged from original) ──
  const filterOrders = (orders, i) => {
    if (!orders) return [];
    return orders.filter((po) => {
      const matchSearch = Object.values(po).some((v) =>
        v?.toString().toLowerCase().includes(searchTerms[i].toLowerCase())
      );
      const poDate   = po.received_date ? new Date(po.received_date) : null;
      const fromDate = filterFrom[i] ? new Date(filterFrom[i]) : null;
      const toDate   = filterTo[i]   ? new Date(filterTo[i])   : null;
      let matchDate  = true;
      if (poDate && fromDate && toDate) matchDate = poDate >= fromDate && poDate <= toDate;
      else if (poDate && fromDate)      matchDate = poDate >= fromDate;
      else if (poDate && toDate)        matchDate = poDate <= toDate;

      const matchLocalSup = !supplierFilters[i] ||
        po.po_inv_items?.some((item) => item.supplier === supplierFilters[i]);
      const matchGlobalSup = !globalSupplierFilter ||
        po.po_inv_items?.some((item) => item.supplier === globalSupplierFilter);

      const matchGood = !goodFilters[i] ||
        po.po_inv_items?.some((item) => item.good === goodFilters[i]);
      const matchUnit = !(unitFilters[i] && goodFilters[i]) ||
        po.po_inv_items?.some((item) => {
          if (item.good !== goodFilters[i]) return false;
          return goods.find((g) => g._id === item.good)?.measurement_unit === unitFilters[i];
        });

      const matchGlobalGood = !globalGoodFilter ||
        po.po_inv_items?.some((item) => item.good === globalGoodFilter);
      const matchGlobalUnit = !(globalUnitFilter && globalGoodFilter) ||
        po.po_inv_items?.some((item) => {
          if (item.good !== globalGoodFilter) return false;
          return goods.find((g) => g._id === item.good)?.measurement_unit === globalUnitFilter;
        });

      return matchSearch && matchDate && matchLocalSup && matchGlobalSup &&
        matchGood && matchUnit && matchGlobalGood && matchGlobalUnit;
    });
  };

  const filteredOrders = allOrders.map((orders, i) => filterOrders(orders, i));
  const filteredTotals = filteredOrders.map((orders) =>
    orders.reduce((acc, po) => acc + (po.total || po.amount || 0), 0)
  );

  // ── Login + fetch PO details (unchanged) ──
  const loginAndGetToken = async () => {
    const res = await axios.post("https://dimspos.com/api/login", {
      email: "mgmt@dimsapp.com", password: "rC2v#rptdwTuk",
    });
    if (!res.data?.token) throw new Error("Cannot retrieve token.");
    return res.data.token;
  };

  const fetchById = async (id, storeId) => {
    if (!id || !storeId) { setFetchedPO(null); setModalLoading(false); return; }
    try {
      setModalLoading(true); setOpenModal(true);
      const token = await loginAndGetToken();
      const url = `https://dimspos.com/api/60a8e221640fac090c3eb0cc/${storeId}/model/store_purchase_orders/id/${id}/received_purchase_orders_details`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setFetchedPO(res?.data?.item);
    } catch (err) {
      console.error(err); setFetchedPO(null);
    } finally { setModalLoading(false); }
  };

  // ── Export Excel (unchanged) ──
  const exportToExcel = (orders, storeName) => {
    const data = orders.map((po, idx) => {
      const supplierId = po.po_inv_items?.find((item) => item.supplier)?.supplier;
      const supplierName = suppliers.find((s) => s._id === supplierId)?.name || "N/A";
      return {
        Ref: `#${po._id ? po._id.slice(0, 8) : po.id || idx}`,
        Supplier: supplierName,
        Date: po.received_date || po.orderDate || "N/A",
        Total: po.total || po.amount?.toFixed(2) || "N/A",
        Comments: po.comments || "N/A",
      };
    });
    const totalAmount = orders.reduce((acc, po) => acc + (po.total || po.amount || 0), 0);
    data.push({ Ref: "Total", Supplier: "", Date: "", Total: totalAmount, Comments: "" });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Orders");
    XLSX.writeFile(wb, `PurchaseOrders_${storeName}_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // ── Export PDF (unchanged) ──
  const exportToPDF = (orders, storeName) => {
    const doc = new jsPDF();
    const now = new Date();
    doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text("Purchase Orders Report", 10, 15);
    doc.setFontSize(12); doc.setFont("helvetica", "normal");
    doc.text(`Store: ${storeName}`, 10, 25);
    doc.text(`Report Date: ${now.toLocaleDateString("en-GB")}`, 10, 32);
    const totalAmount = orders.reduce((acc, po) => acc + (po.total || po.amount || 0), 0);
    doc.text(`Total Orders: ${orders.length}`, 10, 39);
    doc.text(`Total Amount: ${totalAmount.toFixed(2)} AED`, 10, 46);
    autoTable(doc, {
      head: [["Ref", "Supplier", "Date", "Total (AED)", "Comments"]],
      body: orders.map((po, idx) => {
        const supplierId = po.po_inv_items?.find((i) => i.supplier)?.supplier;
        const supplierName = suppliers.find((s) => s._id === supplierId)?.name || "N/A";
        return [`#${po._id ? po._id.slice(0, 8) : po.id || idx}`, supplierName,
          po.received_date || po.orderDate || "N/A", (po.total || po.amount || 0).toFixed(2), po.comments || "N/A"];
      }),
      startY: 55, theme: "grid",
      headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`PurchaseOrders_${storeName}_${now.toISOString().split("T")[0]}.pdf`);
  };

  // ── Generate Invoice PDF (unchanged) ──
  const generateInvoicePDF = (po, storeName) => {
    const doc = new jsPDF();
    doc.setFontSize(20); doc.setFont("helvetica", "bold");
    doc.text("Dimensions Plus", 20, 25);
    doc.setFontSize(12); doc.setFont("helvetica", "normal");
    doc.text("Restaurant Inventory Management System", 20, 35);
    doc.text("Store: " + storeName, 20, 45);
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text("PURCHASE ORDER INVOICE", 20, 65);
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    const poRef = po._id ? po._id.slice(0, 8) : po.id || "N/A";
    const supplierInfo = suppliers.find((s) => s._id === po.po_inv_items?.[0]?.supplier);
    doc.text(`PO Reference: #${poRef}`, 20, 80);
    doc.text(`Date: ${po.received_date || po.orderDate || "N/A"}`, 20, 90);
    doc.text(`Supplier: ${supplierInfo?.name || "N/A"}`, 20, 100);
    doc.text(`Comments: ${po.comments || "N/A"}`, 20, 110);
    doc.line(20, 120, 190, 120);
    const itemsData = goods
      .filter((good) => po.po_inv_items?.some((inv) => inv.good === good._id))
      .map((good, idx) => {
        const poItem = po.po_inv_items.find((inv) => inv.good === good._id);
        const qty = Number(poItem?.quantity) || 0;
        const goodSup = good.goods_suppliers?.[idx] || {};
        const price = Number(goodSup.price) || 0;
        const taxRate = Number(goodSup.tax_rate) || 0;
        const lineTotal = price * qty;
        const taxAmt = lineTotal * (taxRate / 100);
        return [good.name || "N/A", qty.toString(), good.measurement_unit || "N/A",
          `AED ${price.toFixed(2)}`, `AED ${lineTotal.toFixed(2)}`,
          `${taxRate}%`, `AED ${taxAmt.toFixed(2)}`, `AED ${(lineTotal + taxAmt).toFixed(2)}`];
      });
    const totals = goods
      .filter((good) => po.po_inv_items?.some((inv) => inv.good === good._id))
      .reduce((acc, good) => {
        const poItem = po.po_inv_items.find((inv) => inv.good === good._id);
        const qty = Number(poItem?.quantity) || 0;
        const goodSup = good.goods_suppliers?.[0] || {};
        const price = Number(goodSup.price) || 0;
        const taxRate = Number(goodSup.tax_rate) || 0;
        const lineTotal = price * qty;
        acc.subtotal += lineTotal;
        acc.totalTax += lineTotal * (taxRate / 100);
        return acc;
      }, { subtotal: 0, totalTax: 0 });
    autoTable(doc, {
      head: [["Item", "Qty", "Unit", "Price", "Subtotal", "Tax Rate", "Tax Amount", "Total"]],
      body: itemsData, startY: 130, theme: "grid",
      headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: "bold" },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "center" }, 6: { halign: "right" }, 7: { halign: "right", fontStyle: "bold" } },
    });
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    doc.text(`Subtotal: AED ${totals.subtotal.toFixed(2)}`, 140, finalY);
    doc.text(`Total Tax: AED ${totals.totalTax.toFixed(2)}`, 140, finalY + 10);
    doc.text(`Grand Total: AED ${(totals.subtotal + totals.totalTax).toFixed(2)}`, 140, finalY + 20);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text("Generated by DIMS Plus System", 20, finalY + 40);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, finalY + 50);
    doc.save(`Invoice_${poRef}_${storeName}_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  // ── Loading / Error states ──
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" sx={{ background: "#f8fafc" }}>
        <CircularProgress sx={{ color: "#3753b3" }} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" sx={{ background: "#f8fafc" }}>
        <Typography sx={{ color: "#ef4444"}}>Error: {error}</Typography>
      </Box>
    );
  }

  // ── Render per-store table ──
  const renderTable = (i) => {
    const isActive  = selectedStores.includes(i);
    const orders    = filteredOrders[i];
    const storeName = storeNames[i];
    if (!isActive) return null;

    const hasFilters = searchTerms[i] || filterFrom[i] || filterTo[i] ||
      supplierFilters[i] || goodFilters[i];

    return (
      <SectionBox key={i}>
        {/* Section header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
          <div>
            <SectionTitle>
              <StoreIcon sx={{ color: "#3753b3", fontSize: 18 }} />
              {storeName}
              <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: "0.72rem" }}>
                — {orders.length} orders
              </span>
            </SectionTitle>
            <Box display="flex" gap={3}>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Global total: <strong style={{ color: "#0f172a" }}>AED {allTotals[i]?.toFixed(2)}</strong>
              </span>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Filtered total: <strong style={{ color: "#3753b3" }}>AED {filteredTotals[i]?.toFixed(2)}</strong>
              </span>
            </Box>
          </div>
          <Box display="flex" gap={1}>
            <ActionBtn startIcon={<DownloadIcon />} onClick={() => exportToExcel(orders, storeName)}>
              Excel
            </ActionBtn>
            <ActionBtn startIcon={<DownloadIcon />} onClick={() => exportToPDF(orders, storeName)}>
              PDF
            </ActionBtn>
          </Box>
        </Box>

        {/* Filter bar */}
        <FilterBar>
          <TextField
            placeholder="Search…" variant="outlined" size="small"
            value={searchTerms[i]} onChange={(e) => setSearch(i, e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 16 }} /></InputAdornment> }}
            sx={{ ...inputSx, flex: 1, minWidth: 180 }}
          />
          <TextField
            label="From" variant="outlined" size="small" type="date"
            value={filterFrom[i]} onChange={(e) => setFrom(i, e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <InputAdornment position="start"><CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} /></InputAdornment> }}
            sx={{ ...inputSx, minWidth: 160 }}
          />
          <TextField
            label="To" variant="outlined" size="small" type="date"
            value={filterTo[i]} onChange={(e) => setTo(i, e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <InputAdornment position="start"><CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} /></InputAdornment> }}
            sx={{ ...inputSx, minWidth: 160 }}
          />
          <TextField
            label="Supplier" select size="small"
            value={supplierFilters[i]} onChange={(e) => setSupplier(i, e.target.value)}
            sx={{ ...inputSx, minWidth: 200 }} SelectProps={{ MenuProps: menuPropsSx }}
          >
            <MenuItem value="">All Suppliers</MenuItem>
            {suppliers.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
          </TextField>
          <TextField
            label="Good" select size="small"
            value={goodFilters[i]}
            onChange={(e) => { setGood(i, e.target.value); setUnit(i, ""); }}
            sx={{ ...inputSx, minWidth: 200 }} SelectProps={{ MenuProps: menuPropsSx }}
          >
            <MenuItem value="">All Goods</MenuItem>
            {goods.map((g) => <MenuItem key={g._id} value={g._id}>{g.name} ({g.measurement_unit || "N/A"})</MenuItem>)}
          </TextField>
          {goodFilters[i] && (() => {
            const unit = goods.find((g) => g._id === goodFilters[i])?.measurement_unit;
            return unit ? (
              <TextField
                label="Unit" select size="small"
                value={unitFilters[i]} onChange={(e) => setUnit(i, e.target.value)}
                sx={{ ...inputSx, minWidth: 110 }} SelectProps={{ MenuProps: menuPropsSx }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={unit}>{unit}</MenuItem>
              </TextField>
            ) : null;
          })()}
          {hasFilters && (
            <ActionBtn
              startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
              onClick={() => { setSearch(i, ""); setFrom(i, ""); setTo(i, ""); setSupplier(i, ""); setGood(i, ""); setUnit(i, ""); }}
              sx={{ borderColor: "#fecaca", color: "#ef4444", "&:hover": { borderColor: "#ef4444", background: "rgba(239,68,68,0.05)" } }}
            >
              Clear
            </ActionBtn>
          )}
        </FilterBar>

        {/* Table */}
        <TableContainer component={StyledTableContainer}>
          <Table sx={{ ...tableHeadSx, ...tableBodySx }}>
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Good</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders
                .slice(pages[i] * rowsPerPage[i], pages[i] * rowsPerPage[i] + rowsPerPage[i])
                .map((po, idx) => {
                  const effectiveGoodFilter = goodFilters[i] || globalGoodFilter;
                  return (
                    <TableRow key={po._id || po.id || idx}>
                      <TableCell>
                        <RefChip>#{po._id ? po._id.slice(0, 8) : po.id || idx}</RefChip>
                      </TableCell>
                      <TableCell>
                        {suppliers
                          .filter((s) => po.po_inv_items?.some(
                            (inv) => inv.supplier === s._id &&
                              (!effectiveGoodFilter || inv.good === effectiveGoodFilter)
                          ))
                          .map((s) => <div key={s._id} style={{ color: "#0f172a", fontWeight: 600 }}>{s.name}</div>)}
                      </TableCell>
                      <TableCell>
                        {goods
                          .filter((g) => po.po_inv_items?.some((inv) => inv.good === g._id) &&
                            (!effectiveGoodFilter || g._id === effectiveGoodFilter))
                          .map((g) => (
                            <div key={g._id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ color: "#94a3b8" }}>–</span>
                              {g.name}
                              <span style={{ color: "#cbd5e1", fontSize: "0.7rem" }}>({g.measurement_unit})</span>
                            </div>
                          ))}
                      </TableCell>
                      <TableCell>{po.received_date || po.orderDate || "—"}</TableCell>
                      <TableCell>
                        <span style={{ color: "#3753b3", fontWeight: 600 }}>
                          AED {po.total?.toFixed(2) || po.amount?.toFixed(2) || "—"}
                        </span>
                      </TableCell>
                      <TableCell sx={{ color: "#94a3b8 !important", fontStyle: po.comments ? "normal" : "italic" }}>
                        {po.comments || "—"}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <ActionBtn
                            size="small"
                            onClick={() => { setOpenModal(true); fetchById(po?._id, po?.storeId); }}
                            sx={{ borderColor: "rgba(245,158,11,0.3)", color: "whiteSmoke", background: "#3753b3", "&:hover": {color:"whiteSmoke", background: "#647ed1", borderColor: "#3753b3" } }}
                          >
                            Details
                          </ActionBtn>
                          <ActionBtn
                            size="small"
                            startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                            onClick={() => generateInvoicePDF(po, storeName)}
                          >
                            PDF
                          </ActionBtn>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "#94a3b8 !important", padding: "36px !important", fontStyle: "italic" }}>
                    No orders match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={orders.length}
          rowsPerPage={rowsPerPage[i]}
          page={pages[i]}
          onPageChange={(_, newPage) => setPage(i, newPage)}
          onRowsPerPageChange={(e) => { setRPP(i, parseInt(e.target.value, 10)); setPage(i, 0); }}
          sx={{
            color: "#94a3b8",  fontSize: "0.72rem",
            "& .MuiSelect-icon": { color: "#94a3b8" },
            "& .MuiIconButton-root": { color: "#94a3b8", "&:hover": { color: "#3753b3" } },
            "& .Mui-disabled": { color: "#e2e8f0 !important" },
          }}
        />
      </SectionBox>
    );
  };

  // ── Total across selected stores ──
  const grandTotal = selectedStores.reduce((acc, i) => acc + filteredTotals[i], 0);

  return (
    <ThemeProvider theme={theme}>
      <Root>
        {/* ── Page header ── */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
          <TitleBlock>
            <div className="eyebrow">Procurement</div>
            <h1>Purchase Orders</h1>
            <div className="sub">Monitoring & Visualisation — Restaurant Inventory</div>
          </TitleBlock>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <StatCard>
              <div className="val">{selectedStores.length}</div>
              <div className="lbl">Active Stores</div>
            </StatCard>
            <StatCard>
              <div className="val">AED {grandTotal.toFixed(0)}</div>
              <div className="lbl">Filtered Total</div>
            </StatCard>
          </Box>
        </Box>

        {/* ── Global supplier filter ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Global Filters
          </SectionTitle>
          <FilterBar>
            <TextField
              label="Filter all stores by Supplier" select size="small"
              value={globalSupplierFilter} onChange={(e) => setGlobalSupplierFilter(e.target.value)}
              sx={{ ...inputSx, minWidth: 280 }} SelectProps={{ MenuProps: menuPropsSx }}
            >
              <MenuItem value="">All Suppliers</MenuItem>
              {suppliers.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField
              label="Filter all stores by Good" select size="small"
              value={globalGoodFilter}
              onChange={(e) => { setGlobalGoodFilter(e.target.value); setGlobalUnitFilter(""); }}
              sx={{ ...inputSx, minWidth: 280 }} SelectProps={{ MenuProps: menuPropsSx }}
            >
              <MenuItem value="">All Goods</MenuItem>
              {goods.map((g) => <MenuItem key={g._id} value={g._id}>{g.name} ({g.measurement_unit || "N/A"})</MenuItem>)}
            </TextField>
            {globalGoodFilter && (() => {
              const unit = goods.find((g) => g._id === globalGoodFilter)?.measurement_unit;
              return unit ? (
                <TextField
                  label="Unit" select size="small"
                  value={globalUnitFilter} onChange={(e) => setGlobalUnitFilter(e.target.value)}
                  sx={{ ...inputSx, minWidth: 120 }} SelectProps={{ MenuProps: menuPropsSx }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={unit}>{unit}</MenuItem>
                </TextField>
              ) : null;
            })()}
            {(globalSupplierFilter || globalGoodFilter) && (
              <ActionBtn
                startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                onClick={() => { setGlobalSupplierFilter(""); setGlobalGoodFilter(""); setGlobalUnitFilter(""); }}
                sx={{ borderColor: "#fecaca", color: "#ef4444", "&:hover": { borderColor: "#ef4444", background: "rgba(239,68,68,0.05)" } }}
              >
                Clear
              </ActionBtn>
            )}
          </FilterBar>
        </SectionBox>

        {/* ── Store cards ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Select Stores
          </SectionTitle>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            {storeNames.map((name, i) => (
              <StoreCardStyled key={i} active={selectedStores.includes(i)}>
                <CardActionArea onClick={() => toggleStore(i)} sx={{ p: 1.5 }}>
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "center", p: "0 !important" }}>
                    <StoreIcon sx={{ fontSize: 22, color: selectedStores.includes(i) ? "#3753b3" : "#cbd5e1" }} />
                    <Typography sx={{  fontSize: "0.75rem", fontWeight: 700, textAlign: "center", color: "#0f172a" }}>
                      {name}
                    </Typography>
                    <Typography sx={{  fontSize: "0.65rem", color: "#94a3b8" }}>
                      {selectedStores.includes(i) ? "Active" : "Tap to view"}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </StoreCardStyled>
            ))}
          </Box>
        </SectionBox>

        {/* ── Per-store tables ── */}
        {Array.from({ length: NUM_STORES }, (_, i) => renderTable(i))}

        {/* ── PO Details Modal ── */}
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{ sx: { width: "90%", maxWidth: "1200px", borderRadius: 3 } }}
        >
          <DialogTitle sx={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0",  fontWeight: 700, color: "#0f172a" }}>
            Purchase Order Details
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {modalLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress sx={{ color: "#3753b3" }} />
              </Box>
            ) : fetchedPO ? (() => {
              const poData = fetchedPO;
              const invoiceNos = poData.invoice_no
                ? poData.invoice_no.split("\\") && poData.invoice_no?.split("/")
                : [];
              const { subtotal, totalTax } = goods
                .filter((good) => poData.po_inv_items?.some((inv) => inv.good === good._id))
                .reduce((acc, good) => {
                  const poItem = poData.po_inv_items.find((inv) => inv.good === good._id);
                  const qty = Number(poItem?.quantity) || 0;
                  acc.subtotal += (Number(poItem?.price_used) || 0) * qty;
                  acc.totalTax += (Number(poItem?.tax_used) || 0) * qty;
                  return acc;
                }, { subtotal: 0, totalTax: 0 });
              return (
                <>
                  <Box display="flex" gap={3} mt={3.5} flexWrap="wrap">
                    <StatCard>
                      <div className="val">#{poData.order_no || "N/A"}</div>
                      <div className="lbl">Order No</div>
                    </StatCard>
                    <StatCard>
                      <div className="val">{suppliers.find((s) => s._id === poData.po_inv_items?.[0]?.supplier)?.name || "N/A"}</div>
                      <div className="lbl">Supplier</div>
                    </StatCard>
                    <StatCard>
                      <div className="val">{poData.received_date || "N/A"}</div>
                      <div className="lbl">Date</div>
                    </StatCard>
                    <StatCard>
                      <div className="val">{poData.invoice_no || "N/A"}</div>
                      <div className="lbl">Invoice No</div>
                    </StatCard>
                  </Box>
                  {poData.comments && (
                    <Typography sx={{  fontSize: "0.78rem", color: "#64748b", mb: 2 }}>
                      Comments: {poData.comments}
                    </Typography>
                  )}
                  <br/>
                  <TableContainer component={StyledTableContainer}>
                    <Table sx={{ ...tableHeadSx, ...tableBodySx }}>
                      <TableHead>
                        <TableRow>
                          {["Good", "Category", "Price", "Qty", "Buying Unit", "Unit", "Total", "Tax", "Total w/ Tax", "Invoice No"].map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {goods
                          .filter((good) => poData.po_inv_items?.some((inv) => inv.good === good._id))
                          .map((good, idx) => {
                            const poItem = poData.po_inv_items.find((inv) => inv.good === good._id);
                            const qty = Number(poItem?.quantity) || 0;
                            const price = Number(poItem?.price_used) || 0;
                            const tax = Number(poItem?.tax_used) || 0;
                            return (
                              <TableRow key={good._id}>
                                <TableCell sx={{ color: "#0f172a !important", fontWeight: 600 }}>{good.name}</TableCell>
                                <TableCell>{categories.filter((c) => c._id === good.category).map((c) => c.name).join(", ")}</TableCell>
                                <TableCell>{price.toFixed(2)}</TableCell>
                                <TableCell>{qty}</TableCell>
                                <TableCell>{good.goods_suppliers[0]?.buying_unit || "N/A"}</TableCell>
                                <TableCell>{good.measurement_unit || "N/A"}</TableCell>
                                <TableCell sx={{ color: "#3753b3 !important" }}>{(price * qty).toFixed(2)}</TableCell>
                                <TableCell>{(tax * qty).toFixed(2)}</TableCell>
                                <TableCell sx={{ fontWeight: "bold !important" }}>{((price + tax) * qty).toFixed(2)}</TableCell>
                                <TableCell>{invoiceNos[idx] || invoiceNos}</TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                      <TableFooter>
                        <TableRow sx={{ background: "#f8fafc" }}>
                          <TableCell colSpan={6} sx={{  fontWeight: 700, textAlign: "right", color: "#0f172a !important" }}>
                            Totals:
                          </TableCell>
                          <TableCell sx={{  fontWeight: 700, color: "#3753b3 !important" }}>{subtotal.toFixed(2)}</TableCell>
                          <TableCell sx={{  fontWeight: 700, color: "#0f172a !important" }}>{totalTax.toFixed(2)}</TableCell>
                          <TableCell sx={{  fontWeight: 700, color: "#0f172a !important" }}>{(subtotal + totalTax).toFixed(2)}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </>
              );
            })() : (
              <Typography sx={{  color: "#94a3b8", textAlign: "center", p: 4 }}>
                No details available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: "1px solid #e2e8f0", p: 2 }}>
            <ActionBtn onClick={() => setOpenModal(false)}>Close</ActionBtn>
          </DialogActions>
        </Dialog>
      </Root>
    </ThemeProvider>
  );
};

const PurchaseOrders = () => (
  <PurchaseOrdersProvider
    {...Object.fromEntries(
      Array.from({ length: NUM_STORES }, (_, i) => [
        `endpoint${i}`,
        `${baseUrl}/api/api-farooj-index/combined-${i}`,
      ])
    )}
  >
    <PurchaseOrdersTable />
  </PurchaseOrdersProvider>
);

export default PurchaseOrders;